import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useConsumerAuth } from '../../context/ConsumerAuthContext';
import axios from 'axios';
import { 
  Car, Home, CheckCircle, AlertCircle, CreditCard, Shield, 
  ChevronRight, ArrowLeft, DollarSign, FileText, Building
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreditEnhancementApplication = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { consumer, token, loading: authLoading } = useConsumerAuth();
  
  const [step, setStep] = useState(1);
  const [loanType, setLoanType] = useState(searchParams.get('type') || 'auto');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [applicationData, setApplicationData] = useState({
    // Loan Details
    loan_amount: '',
    loan_purpose: '',
    desired_term_months: '60',
    // Employment
    employment_status: 'employed',
    employer_name: '',
    job_title: '',
    years_employed: '',
    annual_income: '',
    // Vehicle (Auto)
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    vehicle_vin: '',
    // Property (Mortgage)
    property_type: 'single_family',
    property_address: '',
    property_value: '',
    down_payment: ''
  });

  useEffect(() => {
    if (!authLoading && !token) {
      navigate('/beth-finance/login');
    }
  }, [token, authLoading, navigate]);

  const fees = {
    auto: { amount: 49, label: 'Auto Loan Enhancement' },
    mortgage: { amount: 199, label: 'Mortgage Enhancement' }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setApplicationData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const validateStep1 = () => {
    if (!applicationData.loan_amount || parseFloat(applicationData.loan_amount) < 1000) {
      setError('Please enter a valid loan amount (minimum $1,000)');
      return false;
    }
    if (!applicationData.loan_purpose) {
      setError('Please select a loan purpose');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!applicationData.employer_name || !applicationData.job_title) {
      setError('Please enter your employment information');
      return false;
    }
    if (!applicationData.annual_income || parseFloat(applicationData.annual_income) < 10000) {
      setError('Please enter a valid annual income');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (loanType === 'auto') {
      if (!applicationData.vehicle_make || !applicationData.vehicle_model || !applicationData.vehicle_year) {
        setError('Please enter vehicle details');
        return false;
      }
    } else {
      if (!applicationData.property_address || !applicationData.property_value) {
        setError('Please enter property details');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    setError('');
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
    else if (step === 3 && validateStep3()) setStep(4);
  };

  const handleSubmitAndPay = async () => {
    setLoading(true);
    setError('');

    try {
      // Create the application
      const response = await axios.post(`${API}/consumer/applications/create`, {
        loan_type: loanType,
        ...applicationData,
        loan_amount: parseFloat(applicationData.loan_amount),
        annual_income: parseFloat(applicationData.annual_income),
        property_value: applicationData.property_value ? parseFloat(applicationData.property_value) : null,
        down_payment: applicationData.down_payment ? parseFloat(applicationData.down_payment) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        // If no checkout URL, go to dashboard
        navigate('/beth-finance/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create application');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#012169]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#012169] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/beth-finance/dashboard" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#E31837] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xl">B</span>
              </div>
              <span className="text-xl font-bold tracking-tight">BETH Finance</span>
            </Link>
            <button 
              onClick={() => navigate('/beth-finance/dashboard')}
              className="flex items-center text-sm hover:text-[#E31837] transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            {['Loan Details', 'Employment', loanType === 'auto' ? 'Vehicle Info' : 'Property Info', 'Review & Pay'].map((label, idx) => (
              <div key={idx} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step > idx + 1 ? 'bg-green-500 text-white' :
                  step === idx + 1 ? 'bg-[#012169] text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > idx + 1 ? <CheckCircle className="w-5 h-5" /> : idx + 1}
                </div>
                {idx < 3 && (
                  <div className={`w-16 md:w-24 h-1 mx-2 ${step > idx + 1 ? 'bg-green-500' : 'bg-gray-200'}`}></div>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs md:text-sm">
            <span className={step >= 1 ? 'text-[#012169] font-medium' : 'text-gray-500'}>Loan Details</span>
            <span className={step >= 2 ? 'text-[#012169] font-medium' : 'text-gray-500'}>Employment</span>
            <span className={step >= 3 ? 'text-[#012169] font-medium' : 'text-gray-500'}>{loanType === 'auto' ? 'Vehicle' : 'Property'}</span>
            <span className={step >= 4 ? 'text-[#012169] font-medium' : 'text-gray-500'}>Review</span>
          </div>
        </div>

        {/* Loan Type Banner */}
        <div className={`rounded-xl p-4 mb-6 flex items-center justify-between ${
          loanType === 'auto' ? 'bg-blue-50 border border-blue-200' : 'bg-purple-50 border border-purple-200'
        }`}>
          <div className="flex items-center">
            {loanType === 'auto' ? (
              <Car className="w-8 h-8 text-blue-600 mr-3" />
            ) : (
              <Home className="w-8 h-8 text-purple-600 mr-3" />
            )}
            <div>
              <h3 className="font-bold text-gray-900">{fees[loanType].label}</h3>
              <p className="text-sm text-gray-600">Credit Enhancement Application</p>
            </div>
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold text-[#E31837]">${fees[loanType].amount}</span>
            <p className="text-xs text-gray-500">one-time fee</p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm p-6">
          {/* Step 1: Loan Details */}
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-[#012169]">Loan Details</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Loan Type</label>
                  <div className="flex space-x-4">
                    <button
                      type="button"
                      onClick={() => setLoanType('auto')}
                      className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                        loanType === 'auto' ? 'border-[#012169] bg-[#012169]/5' : 'border-gray-200'
                      }`}
                    >
                      <Car className={`w-6 h-6 mx-auto mb-2 ${loanType === 'auto' ? 'text-[#012169]' : 'text-gray-400'}`} />
                      <div className="text-sm font-medium">Auto</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setLoanType('mortgage')}
                      className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                        loanType === 'mortgage' ? 'border-[#012169] bg-[#012169]/5' : 'border-gray-200'
                      }`}
                    >
                      <Home className={`w-6 h-6 mx-auto mb-2 ${loanType === 'mortgage' ? 'text-[#012169]' : 'text-gray-400'}`} />
                      <div className="text-sm font-medium">Mortgage</div>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Desired Loan Amount</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="number"
                      name="loan_amount"
                      value={applicationData.loan_amount}
                      onChange={handleChange}
                      placeholder={loanType === 'auto' ? '25000' : '350000'}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none bg-white text-gray-900 placeholder-gray-400"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Loan Purpose</label>
                <select
                  name="loan_purpose"
                  value={applicationData.loan_purpose}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none bg-white text-gray-900"
                >
                  <option value="">Select purpose</option>
                  {loanType === 'auto' ? (
                    <>
                      <option value="new_vehicle">New Vehicle Purchase</option>
                      <option value="used_vehicle">Used Vehicle Purchase</option>
                      <option value="refinance">Refinance Existing Auto Loan</option>
                    </>
                  ) : (
                    <>
                      <option value="purchase">Home Purchase</option>
                      <option value="refinance">Refinance</option>
                      <option value="cash_out">Cash-Out Refinance</option>
                      <option value="construction">Construction Loan</option>
                    </>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Desired Loan Term</label>
                <select
                  name="desired_term_months"
                  value={applicationData.desired_term_months}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none bg-white text-gray-900"
                >
                  {loanType === 'auto' ? (
                    <>
                      <option value="36">36 months (3 years)</option>
                      <option value="48">48 months (4 years)</option>
                      <option value="60">60 months (5 years)</option>
                      <option value="72">72 months (6 years)</option>
                      <option value="84">84 months (7 years)</option>
                    </>
                  ) : (
                    <>
                      <option value="180">15 years</option>
                      <option value="240">20 years</option>
                      <option value="360">30 years</option>
                    </>
                  )}
                </select>
              </div>
            </div>
          )}

          {/* Step 2: Employment Info */}
          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-[#012169]">Employment Information</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employment Status</label>
                <select
                  name="employment_status"
                  value={applicationData.employment_status}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none bg-white text-gray-900"
                >
                  <option value="employed">Employed (W-2)</option>
                  <option value="self_employed">Self-Employed</option>
                  <option value="contractor">Independent Contractor</option>
                  <option value="retired">Retired</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Employer Name</label>
                  <input
                    type="text"
                    name="employer_name"
                    value={applicationData.employer_name}
                    onChange={handleChange}
                    placeholder="Company Inc."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none bg-white text-gray-900 placeholder-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Job Title</label>
                  <input
                    type="text"
                    name="job_title"
                    value={applicationData.job_title}
                    onChange={handleChange}
                    placeholder="Software Engineer"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none bg-white text-gray-900 placeholder-gray-400"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Years at Current Job</label>
                  <input
                    type="number"
                    name="years_employed"
                    value={applicationData.years_employed}
                    onChange={handleChange}
                    placeholder="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none bg-white text-gray-900 placeholder-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Annual Income (Gross)</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="number"
                      name="annual_income"
                      value={applicationData.annual_income}
                      onChange={handleChange}
                      placeholder="85000"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none bg-white text-gray-900 placeholder-gray-400"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Vehicle/Property Info */}
          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-[#012169]">
                {loanType === 'auto' ? 'Vehicle Information' : 'Property Information'}
              </h2>
              
              {loanType === 'auto' ? (
                <>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Year</label>
                      <input
                        type="number"
                        name="vehicle_year"
                        value={applicationData.vehicle_year}
                        onChange={handleChange}
                        placeholder="2024"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none bg-white text-gray-900 placeholder-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Make</label>
                      <input
                        type="text"
                        name="vehicle_make"
                        value={applicationData.vehicle_make}
                        onChange={handleChange}
                        placeholder="Toyota"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none bg-white text-gray-900 placeholder-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                      <input
                        type="text"
                        name="vehicle_model"
                        value={applicationData.vehicle_model}
                        onChange={handleChange}
                        placeholder="Camry"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none bg-white text-gray-900 placeholder-gray-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">VIN (Optional)</label>
                    <input
                      type="text"
                      name="vehicle_vin"
                      value={applicationData.vehicle_vin}
                      onChange={handleChange}
                      placeholder="1HGBH41JXMN109186"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none bg-white text-gray-900 placeholder-gray-400"
                    />
                    <p className="text-xs text-gray-500 mt-1">Vehicle Identification Number (17 characters)</p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Property Type</label>
                    <select
                      name="property_type"
                      value={applicationData.property_type}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none bg-white text-gray-900"
                    >
                      <option value="single_family">Single Family Home</option>
                      <option value="condo">Condominium</option>
                      <option value="townhouse">Townhouse</option>
                      <option value="multi_family">Multi-Family (2-4 units)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Property Address</label>
                    <input
                      type="text"
                      name="property_address"
                      value={applicationData.property_address}
                      onChange={handleChange}
                      placeholder="123 Main Street, City, State 12345"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none bg-white text-gray-900 placeholder-gray-400"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Property Value</label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="number"
                          name="property_value"
                          value={applicationData.property_value}
                          onChange={handleChange}
                          placeholder="450000"
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none bg-white text-gray-900 placeholder-gray-400"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Down Payment</label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="number"
                          name="down_payment"
                          value={applicationData.down_payment}
                          onChange={handleChange}
                          placeholder="90000"
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none bg-white text-gray-900 placeholder-gray-400"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 4: Review & Pay */}
          {step === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-[#012169]">Review Your Application</h2>
              
              <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                  <div className="flex items-center">
                    {loanType === 'auto' ? <Car className="w-6 h-6 text-[#012169] mr-3" /> : <Home className="w-6 h-6 text-[#012169] mr-3" />}
                    <span className="font-bold">{fees[loanType].label}</span>
                  </div>
                  <span className="font-bold text-[#E31837]">{formatCurrency(applicationData.loan_amount)}</span>
                </div>

                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Purpose:</span>
                    <span className="ml-2 text-gray-900 capitalize">{applicationData.loan_purpose?.replace('_', ' ')}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Term:</span>
                    <span className="ml-2 text-gray-900">{applicationData.desired_term_months} months</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Employer:</span>
                    <span className="ml-2 text-gray-900">{applicationData.employer_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Annual Income:</span>
                    <span className="ml-2 text-gray-900">{formatCurrency(applicationData.annual_income)}</span>
                  </div>
                  {loanType === 'auto' && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Vehicle:</span>
                      <span className="ml-2 text-gray-900">
                        {applicationData.vehicle_year} {applicationData.vehicle_make} {applicationData.vehicle_model}
                      </span>
                    </div>
                  )}
                  {loanType === 'mortgage' && (
                    <>
                      <div className="col-span-2">
                        <span className="text-gray-500">Property:</span>
                        <span className="ml-2 text-gray-900">{applicationData.property_address}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Property Value:</span>
                        <span className="ml-2 text-gray-900">{formatCurrency(applicationData.property_value)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Payment Summary */}
              <div className="bg-[#012169] text-white rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4">Payment Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-300">Credit Enhancement Fee</span>
                    <span>${fees[loanType].amount}.00</span>
                  </div>
                  <div className="border-t border-white/20 pt-3 flex justify-between text-lg font-bold">
                    <span>Total Due Today</span>
                    <span className="text-[#E31837]">${fees[loanType].amount}.00</span>
                  </div>
                </div>
                <div className="mt-4 flex items-center text-sm text-gray-300">
                  <Shield className="w-4 h-4 mr-2" />
                  Secure payment powered by Stripe
                </div>
              </div>

              {/* Benefits Reminder */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <h4 className="font-medium text-green-800 mb-2">What you'll get:</h4>
                <ul className="space-y-1 text-sm text-green-700">
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2" /> Enhanced credit profile for lenders</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2" /> Access to 50+ partner lenders</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2" /> Multiple loan offers to compare</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2" /> Dedicated loan specialist support</li>
                </ul>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            {step > 1 ? (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-all"
              >
                Back
              </button>
            ) : (
              <button
                type="button"
                onClick={() => navigate('/beth-finance/dashboard')}
                className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
            )}

            {step < 4 ? (
              <button
                type="button"
                onClick={handleNext}
                className="px-8 py-3 bg-[#012169] hover:bg-[#01337d] text-white rounded-lg font-semibold transition-all flex items-center"
              >
                Continue <ChevronRight className="w-5 h-5 ml-1" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmitAndPay}
                disabled={loading}
                className="px-8 py-3 bg-[#E31837] hover:bg-[#c4142f] text-white rounded-lg font-semibold transition-all flex items-center disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay ${fees[loanType].amount} & Submit
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CreditEnhancementApplication;
